import { CasaviFilterElement, CasaviFilterType } from "@/api-schema-enums";
import { useCasaviContactBlacklistSearchQuery } from "@/common/api/casavi/contact-blacklist/use-casavi-contact-blacklist-search.query";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCardActions } from "@/components/ui/form-card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  normalizeCasaviFilterElement,
  normalizeCasaviFilterType,
} from "@/modules/casavi/_helpers/casavi-filter-form-values";
import { validateAndSubmit } from "@/modules/casavi/_helpers/validation-toast";
import { useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import {
  CasaviContactBlacklistFormSchemaType,
  validateCasaviContactBlacklistDuplicate,
  validateCasaviContactBlacklistUpdateDuplicate,
  validateRegexPattern,
} from "../_helpers/validate-casavi-contact-blacklist";

interface ContactBlacklistFormFieldsProps {
  form: UseFormReturn<CasaviContactBlacklistFormSchemaType>;
  onSubmit: (values: CasaviContactBlacklistFormSchemaType) => void;
  isPending: boolean;
  mode: "create" | "update";
  currentUuid?: string;
}

export function ContactBlacklistFormFields({
  form,
  onSubmit,
  isPending,
  mode,
  currentUuid,
}: ContactBlacklistFormFieldsProps) {
  const { t } = useTranslation(["casavi", "common"]);
  const [_, navigate] = useLocation();
  const formValues = form.watch();
  const normalizedElement = normalizeCasaviFilterElement(formValues.element);
  const normalizedType = normalizeCasaviFilterType(formValues.type);

  useEffect(() => {
    if (formValues.element && normalizedElement && formValues.element !== normalizedElement) {
      form.setValue("element", normalizedElement, { shouldDirty: false, shouldValidate: true });
    }
  }, [form, formValues.element, normalizedElement]);

  useEffect(() => {
    if (formValues.type && normalizedType && formValues.type !== normalizedType) {
      form.setValue("type", normalizedType, { shouldDirty: false, shouldValidate: true });
    }
  }, [form, formValues.type, normalizedType]);

  const { data: existingContactBlacklists } = useCasaviContactBlacklistSearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
      },
    },
  });

  const duplicateValidation = useMemo(() => {
    if (mode === "update") {
      return validateCasaviContactBlacklistUpdateDuplicate(existingContactBlacklists?.data, currentUuid, formValues);
    }
    return validateCasaviContactBlacklistDuplicate(existingContactBlacklists?.data, formValues);
  }, [existingContactBlacklists, formValues, currentUuid, mode]);

  const regexValidationError = useMemo(() => {
    if (formValues.type === CasaviFilterType.Regex && formValues.value && !validateRegexPattern(formValues.value)) {
      return t("common:InvalidRegexPattern");
    }
    return null;
  }, [formValues.type, formValues.value, t]);

  const handleSubmitWithValidation = validateAndSubmit<CasaviContactBlacklistFormSchemaType>(
    duplicateValidation.hasErrors,
    duplicateValidation.errors,
    onSubmit,
  );

  const isSubmitDisabled =
    isPending || duplicateValidation.hasErrors || !form.formState.isValid || !!regexValidationError;

  const submitButtonText = mode === "create" ? t("casavi:Add") : t("common:SaveChanges");

  return (
    <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmitWithValidation)}>
      <Card>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="element"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("casavi:Element")}</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={normalizeCasaviFilterElement(field.value) ?? field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("casavi:SelectElement")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={CasaviFilterElement.Id}>{t("casavi:ElementId")}</SelectItem>
                      <SelectItem value={CasaviFilterElement.Email}>{t("casavi:ElementEmail")}</SelectItem>
                      <SelectItem value={CasaviFilterElement.FirstName}>{t("common:FirstName")}</SelectItem>
                      <SelectItem value={CasaviFilterElement.LastName}>{t("common:LastName")}</SelectItem>
                      <SelectItem value={CasaviFilterElement.CompanyName}>{t("casavi:ElementCompanyName")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("casavi:Type")}</FormLabel>
                  <Select onValueChange={field.onChange} value={normalizeCasaviFilterType(field.value) ?? field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("casavi:SelectType")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={CasaviFilterType.Regex}>{t("casavi:TypeRegex")}</SelectItem>
                      <SelectItem value={CasaviFilterType.Contains}>{t("casavi:TypeContains")}</SelectItem>
                      <SelectItem value={CasaviFilterType.Full}>{t("casavi:TypeFull")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="value"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("casavi:Value")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={formValues.type === CasaviFilterType.Regex ? "^\\d{5}$" : t("casavi:EnterValue")}
                    {...field}
                    error={duplicateValidation.hasErrors || !!regexValidationError}
                  />
                </FormControl>
                <FormMessage />
                {duplicateValidation.hasErrors && (
                  <p className="text-error mt-1 text-sm">{duplicateValidation.errors[0]}</p>
                )}
                {regexValidationError && <p className="text-error mt-1 text-sm">{regexValidationError}</p>}
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="caseSensitive"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-y-0 space-x-3">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>{t("casavi:CaseSensitive")}</FormLabel>
                </div>
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      <FormCardActions
        cancelLabel={t("common:Cancel")}
        onCancel={() => navigate("~/cas/contact-blacklist")}
        isPending={isPending}
        submitDisabled={isSubmitDisabled}
        submitLabel={submitButtonText}
      />
    </form>
  );
}
